import { zodResolver } from "@hookform/resolvers/zod"
import { Dialog, DialogActions, DialogContent } from "@mui/material"
import type { FC } from "react"
import { useCallback, useState } from "react"
import { FormProvider, useForm } from "react-hook-form"

import type { GlAccountFormValues } from "../../schemas/glAccountForm"
import { glAccountFormSchema } from "../../schemas/glAccountForm"
import { toGlAccountFormValues } from "../../utils/toGlAccountFormValues"
import { GlAccountForm } from "../GlAccountForm/GlAccountForm"

import { Button } from "@/components/common/Button"
import { CancelDialog } from "@/components/common/Modals"
import DialogHeader from "@/components/common/Modals/DialogHeader"
import type { GlAccount } from "@/graphql/codegen/graphql"
import type { ActionOutcome } from "@/types/common"

interface GlAccountFormDialogProps {
  onClose: (shouldRefetch?: boolean) => void
  onSubmit: (formValues: GlAccountFormValues, glAccountId?: string | null) => Promise<ActionOutcome>
  account?: GlAccount | null
}

export const GlAccountFormDialog: FC<GlAccountFormDialogProps> = ({ onClose, onSubmit, account }) => {
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false)
  const isEditMode = !!account

  const initialParentOption =
    account?.parentGlAccountId && account.parent
      ? { value: account.parentGlAccountId, label: `${account.parent.number} - ${account.parent.title}` }
      : null

  const methods = useForm<GlAccountFormValues>({
    resolver: zodResolver(glAccountFormSchema),
    defaultValues: toGlAccountFormValues(account),
    mode: "all",
  })

  const {
    handleSubmit,
    setError,
    formState: { isValid, isDirty, isSubmitting },
  } = methods

  const canSubmit = isValid && isDirty && !isSubmitting

  const handleFormSubmit = useCallback(
    async (formValues: GlAccountFormValues) => {
      const result = await onSubmit(formValues, account?.glAccountId)

      if (result.success) {
        onClose(true)
      } else if (result.error) {
        let errorMessage = result.error.message
        if (errorMessage.includes("sub number") && errorMessage.includes("already exists")) {
          errorMessage = "This posting account already exists"
          setError("subNumber", { type: "manual", message: errorMessage })
        } else if (errorMessage.includes("already exists")) {
          errorMessage = "This parent account already exists"
        }

        setError("number", {
          type: "manual",
          message: errorMessage,
        })
      }
    },
    [onSubmit, setError, onClose, account?.glAccountId]
  )

  const dialogTitle = isEditMode ? "Edit General Ledger Account" : "Add General Ledger Account"

  const handleClose = useCallback(() => {
    if (isDirty) {
      setIsCancelDialogOpen(true)
    } else {
      onClose()
    }
  }, [isDirty, onClose])

  const handleCancelDialogClose = () => setIsCancelDialogOpen(false)

  const handleCancelConfirm = () => {
    onClose()
  }

  if (isCancelDialogOpen) {
    return <CancelDialog onClose={handleCancelDialogClose} onConfirm={handleCancelConfirm} />
  }

  return (
    <Dialog open onClose={handleClose} maxWidth='sm' fullWidth data-testid='gl-account-form-dialog'>
      <DialogHeader title={dialogTitle} onClose={handleClose} />
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(handleFormSubmit)}>
          <DialogContent className='pt-4'>
            <GlAccountForm isEditMode={isEditMode} initialParentOption={initialParentOption} />
          </DialogContent>

          <DialogActions className='px-6 pb-6'>
            <Button appearance='contained' onClick={handleClose} data-testid='gl-account-cancel-button'>
              Cancel
            </Button>
            <Button type='submit' appearance='contained' disabled={!canSubmit} data-testid='gl-account-save-button'>
              Save
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  )
}
